import { Fragment, ReactNode } from "react";
import { Link } from "react-router-dom";
import useI18n from "../../hooks/useTranslations";
import { CustomError, isForbidden, isNotFound } from "../../http/errors";
import styles from "./Header.module.css";

export interface HeaderPath {
  display: string;
  url?: string;
}

type HeaderProps = {
  title: string;
  path?: HeaderPath | HeaderPath[];
  buttons?: ReactNode | ReactNode[];
  error?: CustomError;
};

const Header = ({ buttons, ...rest }: HeaderProps) => {
  return (
    <div className={styles.header}>
      <Heading {...rest} />
      {buttons &&
        (Array.isArray(buttons) ? buttons : [buttons])?.map((b, index) => (
          <Fragment key={`button_${index}`}>{b}</Fragment>
        ))}
    </div>
  );
};

const Heading = ({ title, path, error }: Omit<HeaderProps, "buttons">) => {
  const i18n = useI18n();
  const pathItems = path ? (Array.isArray(path) ? path : [path]) : [];
  const crumbs = pathItems.filter((x) => x.display);

  const errorText = isNotFound(error)
    ? i18n.translation.common.resourceNotFound
    : isForbidden(error)
      ? i18n.translation.common.youAreNotAllowed
      : error
        ? i18n.translation.common.genericError
        : undefined;

  const titleText = isNotFound(error)
    ? `${i18n.translation.common.notFound}`
    : isForbidden(error)
      ? i18n.translation.common.forbidden
      : error
        ? i18n.translation.common.error
        : title;

  const lastCrumbIndex = crumbs.length - 1;

  return (
    <div className={styles.heading}>
      <div className={styles.h2}>
        <div className={styles.title}>
          <div className={styles.bar}></div>
          <div>{titleText}</div>
        </div>
      </div>

      {(!!crumbs.length || !!errorText) && (
        <nav className={styles.path} aria-label={i18n.translation.common.breadcrumb}>
          <ol>
            {crumbs.map((x, index) => {
              const isCurrent = !errorText && index === lastCrumbIndex;
              return (
                <li key={`p_${index}`} aria-current={isCurrent ? "page" : undefined} title={x.display}>
                  {x.url && !isCurrent ? <Link to={x.url}>{x.display}</Link> : x.display}
                </li>
              );
            })}
            {errorText && (
              <li className={styles.error} aria-current="page">
                {errorText}
              </li>
            )}
          </ol>
        </nav>
      )}
    </div>
  );
};

Header.Heading = Heading;

export default Header;
